import { useAuth } from '@/app/context';
import { type MenuItem } from '@/shared/constants';
import { useRequirePermissions } from '@/shared/hooks';
import { Link, useRouterState } from '@tanstack/react-router';
import { isMenuPathActive } from './menu-active.util';

interface SidebarMenuItemProps {
  item: MenuItem;
  isExpanded: boolean;
  onMenuClick: () => void;
  permissions?: string[];
}

export function SidebarMenuItem({
  item,
  isExpanded,
  onMenuClick,
  permissions,
}: SidebarMenuItemProps) {
  const { hasPermission, isChecking } = useRequirePermissions({
    permissions: permissions || [],
  });
  const { groups: userGroups } = useAuth();

  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const isActive = isMenuPathActive(currentPath, item.id);
  const Icon = item.icon;

  if (permissions && permissions.length > 0) {
    if (isChecking) {
      return null;
    }
    if (!hasPermission) {
      return null;
    }
  }

  if (item.groups && item.groups.length > 0) {
    if (!userGroups?.some(g => item.groups!.includes(g))) {
      return null;
    }
  }

  return (
    <Link
      to={item.id}
      data-active={isActive ? 'true' : undefined}
      onClick={onMenuClick}
      className={`
        relative w-full h-10 rounded-md flex items-center gap-3 transition-all duration-300 ease-in-out
        ${isExpanded ? 'px-3' : 'px-2.5'}
        ${
          isActive
            ? 'bg-alba-green-500 text-white'
            : 'text-white hover:bg-alba-blue-700'
        }
      `}
    >
      <Icon className='w-5 h-5 flex-shrink-0' strokeWidth={1.6} />
      <span
        className={`transition-opacity duration-300 whitespace-nowrap text-sm font-medium overflow-hidden flex-1 text-left ${
          isExpanded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {item.label}
      </span>
    </Link>
  );
}
